/**
 * FMP (Financial Modeling Prep) stock OHLC service.
 * Replaces Polygon for historical bars and real-time stock quotes when FMP_API_KEY is set.
 * Used by live chart and hybrid gamma; options WebSocket remains on Polygon.
 */

const FMP_BASE = 'https://financialmodelingprep.com/stable';
const FMP_WS_URL = 'wss://financialmodelingprep.com/ws/us-stocks';

export function getFmpApiKey(): string {
  const env = typeof process !== 'undefined' ? process.env : ({} as NodeJS.ProcessEnv);
  return (env.REACT_APP_FMP_API_KEY || env.FMP_API_KEY || '').trim();
}

export function isFmpEnabled(): boolean {
  const key = getFmpApiKey();
  return key.length >= 10 && key !== 'demo_key';
}

/** Same shape as Polygon for drop-in replacement */
export interface OHLCBar {
  time: number;
  open: number;
  high: number;
  low: number;
  close: number;
  volume: number;
  vwap?: number;
  transactions?: number;
}

/** FMP historical-chart response item (field names may vary) */
interface FmpChartItem {
  date?: string;
  open?: number;
  high?: number;
  low?: number;
  close?: number;
  volume?: number;
  [k: string]: unknown;
}

/**
 * Fetch historical intraday OHLC from FMP. Returns bars in Polygon-compatible shape.
 * Intervals: 1, 5, 15, 30, 60 (minutes). For daily use 5min and aggregate on client or use a different endpoint.
 */
export async function getFmpHistoricalOhlc(
  symbol: string,
  intervalMinutes: string,
  fromDate: string,
  toDate: string,
  limit: number = 5000
): Promise<OHLCBar[]> {
  const key = getFmpApiKey();
  if (!key || key.length < 10) return [];

  const interval = intervalMinutes === 'D' ? '60' : intervalMinutes;
  const pathMap: Record<string, string> = {
    '1': 'historical-chart/1min',
    '5': 'historical-chart/5min',
    '15': 'historical-chart/15min',
    '30': 'historical-chart/30min',
    '60': 'historical-chart/1hour'
  };
  const path = pathMap[interval] || 'historical-chart/5min';
  const params = new URLSearchParams({
    symbol: symbol.toUpperCase(),
    from: fromDate,
    to: toDate,
    apikey: key
  });
  const url = `${FMP_BASE}/${path}?${params.toString()}`;

  try {
    const res = await fetch(url);
    if (!res.ok) return [];
    const raw = await res.json();
    const data = Array.isArray(raw)
      ? raw
      : (raw?.results ?? raw?.data ?? raw?.historical ?? []);
    if (!Array.isArray(data)) return [];

    const bars: OHLCBar[] = [];
    for (const row of data as FmpChartItem[]) {
      const dateStr = row.date ?? row.timestamp ?? row.Date;
      if (!dateStr) continue;
      const time = typeof dateStr === 'number' ? dateStr : new Date(String(dateStr)).getTime();
      const open = Number(row.open ?? row.o ?? 0);
      const high = Number(row.high ?? row.h ?? 0);
      const low = Number(row.low ?? row.l ?? 0);
      const close = Number(row.close ?? row.c ?? 0);
      const volume = Number(row.volume ?? row.v ?? 0);
      if (!time || (!open && !high && !low && !close)) continue;
      bars.push({
        time,
        open,
        high,
        low,
        close,
        volume,
        vwap: row.vwap != null ? Number(row.vwap) : undefined
      });
    }
    bars.sort((a, b) => a.time - b.time);
    return limit > 0 ? bars.slice(-limit) : bars;
  } catch (_) {
    return [];
  }
}

/** Polygon-compatible aggregate so chart code can stay unchanged */
export interface StockAggregateLike {
  ev: 'A';
  sym: string;
  s: number;
  e: number;
  o: number;
  h: number;
  l: number;
  c: number;
  v: number;
}

import type { ChartStockLiveCallback } from './chartStockLiveTypes';
type ConnectionCallback = (connected: boolean) => void;

/**
 * FMP WebSocket sends real-time quote updates (not pre-aggregated minute bars).
 * We convert each quote to a single-tick "aggregate" so existing chart logic works.
 */
export class FmpStockWebSocketService {
  private ws: WebSocket | null = null;
  private apiKey: string = '';
  private callbacks = new Map<string, ChartStockLiveCallback[]>();
  private connectionCallbacks: ConnectionCallback[] = [];
  private subscribed = new Set<string>();
  private reconnectAttempts = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private isConnecting = false;

  constructor(apiKey: string) {
    this.apiKey = (apiKey || getFmpApiKey()).trim();
  }

  onConnectionChange(cb: ConnectionCallback): void {
    this.connectionCallbacks.push(cb);
  }

  isConnected(): { stock: boolean } {
    return { stock: this.ws?.readyState === WebSocket.OPEN };
  }

  connectStockAggregates(): Promise<void> {
    if (this.ws?.readyState === WebSocket.OPEN) return Promise.resolve();
    if (this.isConnecting) return Promise.resolve();
    if (!this.apiKey || this.apiKey.length < 10) {
      return Promise.resolve();
    }

    this.isConnecting = true;
    const url = `${FMP_WS_URL}?apikey=${encodeURIComponent(this.apiKey)}`;

    return new Promise((resolve) => {
      try {
        this.ws = new WebSocket(url);
      } catch (e) {
        this.isConnecting = false;
        resolve();
        return;
      }

      this.ws.onopen = () => {
        this.isConnecting = false;
        this.reconnectAttempts = 0;
        this.sendLogin();
        this.connectionCallbacks.forEach((cb) => cb(true));
        this.subscribed.forEach((sym) => this.sendSubscribe(sym));
        resolve();
      };

      this.ws.onmessage = (event) => {
        try {
          const raw = typeof event.data === 'string' ? event.data : '';
          const parsed = raw ? JSON.parse(raw) : null;
          if (!parsed) return;
          const list = Array.isArray(parsed) ? parsed : [parsed];
          for (const item of list) {
            if (!item || typeof item !== 'object') continue;
            const row = item as Record<string, unknown>;
            const payload =
              row.data && typeof row.data === 'object'
                ? (row.data as Record<string, unknown>)
                : row;
            this.handleMessage(payload);
          }
        } catch (_) {
          // ignore parse errors
        }
      };

      this.ws.onclose = () => {
        this.isConnecting = false;
        this.connectionCallbacks.forEach((cb) => cb(false));
        this.ws = null;
        this.tryReconnect();
        resolve();
      };

      this.ws.onerror = () => {
        this.isConnecting = false;
        resolve();
      };
    });
  }

  private sendLogin(): void {
    if (this.ws?.readyState !== WebSocket.OPEN || !this.apiKey) return;
    const msg = JSON.stringify({ event: 'login', data: { apiKey: this.apiKey } });
    this.ws.send(msg);
  }

  private sendSubscribe(symbol: string): void {
    if (this.ws?.readyState !== WebSocket.OPEN) return;
    const msg = JSON.stringify({ event: 'subscribe', data: { ticker: [symbol] } });
    this.ws.send(msg);
  }

  private sendUnsubscribe(symbol: string): void {
    if (this.ws?.readyState !== WebSocket.OPEN) return;
    const msg = JSON.stringify({ event: 'unsubscribe', data: { ticker: [symbol] } });
    this.ws.send(msg);
  }

  private tryReconnect(): void {
    if (this.reconnectAttempts >= 10) return;
    this.reconnectTimer = setTimeout(() => {
      this.reconnectAttempts++;
      this.connectStockAggregates();
    }, 3000);
  }

  private handleMessage(data: Record<string, unknown>): void {
    const sym = (data.s ?? data.symbol ?? data.S ?? '').toString().toUpperCase();
    if (!sym) return;
    const price = Number(data.lp ?? data.p ?? data.price ?? data.c ?? data.close ?? 0);
    const volume = Number(data.ls ?? data.v ?? data.volume ?? 0);
    let ts = Number(data.t ?? data.timestamp ?? data.time ?? Date.now());
    if (ts < 1e12) ts *= 1000;
    const agg: StockAggregateLike = {
      ev: 'A',
      sym,
      s: ts,
      e: ts,
      o: price,
      h: price,
      l: price,
      c: price,
      v: volume
    };
    const list = this.callbacks.get(sym);
    if (list) list.forEach((cb) => { try { cb(agg); } catch (_) {} });
  }

  subscribeToStock(symbol: string, callback: ChartStockLiveCallback): void {
    const sym = symbol.trim().toUpperCase();
    if (!this.callbacks.has(sym)) this.callbacks.set(sym, []);
    this.callbacks.get(sym)!.push(callback);
    this.subscribed.add(sym);
    if (this.ws?.readyState === WebSocket.OPEN) this.sendSubscribe(sym);
    else this.connectStockAggregates().then(() => this.sendSubscribe(sym));
  }

  unsubscribeFromStock(symbol: string, callback?: ChartStockLiveCallback): void {
    const sym = symbol.trim().toUpperCase();
    const list = this.callbacks.get(sym);
    if (!list) return;
    if (callback) {
      const i = list.indexOf(callback);
      if (i >= 0) list.splice(i, 1);
    }
    if (!callback || list.length === 0) {
      this.callbacks.delete(sym);
      this.subscribed.delete(sym);
      this.sendUnsubscribe(sym);
    }
  }
}
